import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { List, ListItem, ListItemIcon, ListItemText, Stack, TextareaAutosize, Typography, useMediaQuery } from '@mui/material';
import { ReactComponent as EmailIcon } from '../../assets/svg/EmailIcon.svg';
import CustomBtn from 'ui-component/Button';
import useAuth from 'hooks/useAuth';
import InputField from 'ui-component/InputField';
import { emailRgx } from 'utils/constant';
import ErrorMsg from 'ui-component/ErrorMsg';
import axios from 'axios';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ContactUs = ({ open, handleClose, switchComponent, setSwitchComponent, errorMessage }: any) => {
    const { isLoggedIn, user } = useAuth();
    const [email, setEmail] = React.useState('');
    const [message, setMessage] = React.useState('');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [emailError, setEmailError] = React.useState<boolean>(false);
    const [apiSuccess, setApiSuccess] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const isMobile = useMediaQuery('(max-width:900px)');
    const [errorMsg, setErrorMsg] = React.useState('');

    const handlePostRequest = async () => {
        try {
            setLoading(true);

            await axios.post(`${process.env.REACT_APP_API_URL}/loans/contact-us`, {
                firstName,
                lastName,
                email,
                message: `${message},${errorMessage}`,
                source: 'website'
            });

            setLoading(false);
            setApiSuccess(true);
            setSwitchComponent(true);
            setFirstName('');
            setLastName('');
            setEmailError(false);
            setErrorMsg('');
        } catch (e: any) {
            setErrorMsg(e?.response?.data?.msg);
            setLoading(false);
            setApiSuccess(false);
        }
    };
    React.useEffect(() => {
        if (apiSuccess) {
            setSwitchComponent(true);
        }
    }, [apiSuccess]);

    React.useEffect(() => {
        if (user) {
            setFirstName(user.firstName);
            setLastName(user.lastName);
            setEmail(user.email);
        }
    }, []);
    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogTitle>{switchComponent ? 'Thanks for reaching out!' : 'Have a Question?'}</DialogTitle>
            <DialogContent>
                {switchComponent && (
                    <Typography component="h1" mt={3} mb={3} fontWeight="bold">
                        Your question has been sent to BrightUp Customer Support.
                    </Typography>
                )}
                <DialogContentText id="alert-dialog-slide-description">
                    {switchComponent
                        ? `We’ll be in touch soon! Remember to check your email inbox for the follow up. 
                            Have a fantastic day! `
                        : `Get help from a real person with BrightUp Customer Support. Describe your issue or question below, and we’ll respond
                        directly to your email inbox.`}
                </DialogContentText>
                {!switchComponent && (
                    <>
                        {isLoggedIn ? (
                            <List sx={{ backgroundColor: '#D9D9D980', borderRadius: '7px', marginTop: '26px', marginBottom: '41px' }}>
                                <ListItem sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                    <ListItemIcon>
                                        <EmailIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography component="span" variant="body2" color="#707479">
                                                BrightUp Email/Username
                                            </Typography>
                                        }
                                        secondary={
                                            <React.Fragment>
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    color="text.primary"
                                                    sx={{ wordBreak: 'break-word' }}
                                                >
                                                    {user?.email}
                                                </Typography>
                                            </React.Fragment>
                                        }
                                    />
                                </ListItem>
                            </List>
                        ) : (
                            <>
                                <Stack justifyContent="space-around" mt={2} sx={{ flexDirection: { sm: 'column', md: 'row' } }}>
                                    <InputField
                                        onChange={(e: any) => {
                                            setFirstName(e.target.value);
                                        }}
                                        label="First Name"
                                        sx={{
                                            marginRight: '24px',
                                            '& .MuiFilledInput-root': {
                                                background: 'white',
                                                border: '2px solid #119FC0',
                                                borderRadius: '12px'
                                            },
                                            '& .MuiFilledInput-root.Mui-focused': {
                                                borderColor: '#119FC0'
                                            }
                                        }}
                                    />
                                    <InputField
                                        label="Last Name"
                                        onChange={(e: any) => {
                                            setLastName(e.target.value);
                                        }}
                                        sx={{
                                            marginTop: isMobile ? '24px' : '0px',
                                            '& .MuiFilledInput-root': {
                                                background: 'white',
                                                border: '2px solid #119FC0',
                                                borderRadius: '12px'
                                            },
                                            '& .MuiFilledInput-root.Mui-focused': {
                                                borderColor: '#119FC0'
                                            }
                                        }}
                                    />
                                </Stack>
                                <InputField
                                    value={email}
                                    name="email"
                                    type="email"
                                    onChange={(e: any) => {
                                        setEmail(e.target.value);
                                        if (!emailRgx.test(e.target.value)) {
                                            setEmailError(true);
                                        } else {
                                            setEmailError(false);
                                        }
                                    }}
                                    label="Email Address"
                                    sx={{
                                        marginTop: '24px',
                                        '& .MuiFilledInput-root': {
                                            background: 'white',
                                            border: '2px solid #119FC0',
                                            borderRadius: '12px'
                                        },
                                        '& .MuiFilledInput-root.Mui-focused': {
                                            borderColor: '#119FC0'
                                        }
                                    }}
                                />
                                {emailError && <ErrorMsg triangleStyles={{ marginLeft: '21px' }} label="Please enter a valid email ID." />}
                            </>
                        )}
                        <Typography mt={3}>How can we help?</Typography>
                        <TextareaAutosize
                            aria-label="empty textarea"
                            placeholder="Typing..."
                            minRows={3}
                            style={{
                                width: '100%',
                                border: '2px solid #119FC0',
                                borderRadius: '4px',
                                marginTop: '20px',
                                padding: '10px'
                            }}
                            onChange={(e) => {
                                console.log('e.target.value', e.target.value);
                                setMessage(e.target.value);
                            }}
                        />
                        <Typography sx={{ marginTop: '10px', color: 'red' }}>{errorMsg}</Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <CustomBtn
                    label={switchComponent ? 'Close' : 'Send'}
                    disabled={
                        (emailError && !switchComponent) ||
                        loading ||
                        (!switchComponent && (email.trim() === '' || firstName.trim() === '' || lastName.trim() === ''))
                    }
                    sx={{ marginInline: '16px' }}
                    onPress={async () => {
                        if (!switchComponent) {
                            await handlePostRequest();
                        } else {
                            setSwitchComponent(false);
                            handleClose();
                        }
                    }}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ContactUs;
